import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Button, ModalShell } from '@/components/ui';
import { PhoneInput, normalizePhone } from '@/components/ui/common/PhoneInput';
import { validateEmail } from '@/utils/validation/email.validator';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { Text } from '@/components/ui';
import { FormStack, FormField } from '@/components/ui';
import { GuideButton } from '@/features/guide';

type RegistrationFormMode = 'admin' | 'adminEdit' | 'siteManager';

export interface RegistrationFormData {
  email: string;
  user_name?: string;
  company_name?: string;
  phone?: string;
  notes?: string;
  planned_device_limit?: number;
  planned_duration_months?: number;
}

interface RegistrationFormModalProps {
  isOpen: boolean;
  mode: RegistrationFormMode;
  initialData?: Partial<RegistrationFormData>;
  onClose: () => void;
  onSubmit: (data: RegistrationFormData) => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
}

export const RegistrationFormModal: React.FC<RegistrationFormModalProps> = ({
  isOpen,
  mode,
  initialData = {},
  onClose,
  onSubmit,
  isLoading = false,
  title,
  description,
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<RegistrationFormData>({
    email: initialData.email || '',
    user_name: initialData.user_name || '',
    company_name: initialData.company_name || '',
    phone: normalizePhone(initialData.phone || ''),
    notes: initialData.notes || '',
    planned_device_limit: initialData.planned_device_limit ?? 10,
    planned_duration_months: initialData.planned_duration_months ?? 12,
  });
  const [phoneValid, setPhoneValid] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const normalizedPhone = normalizePhone(initialData.phone || '');
      setFormData({
        email: initialData.email || '',
        user_name: initialData.user_name || '',
        company_name: initialData.company_name || '',
        phone: normalizedPhone,
        notes: initialData.notes || '',
        planned_device_limit: initialData.planned_device_limit ?? 10,
        planned_duration_months: initialData.planned_duration_months ?? 12,
      });
      setPhoneValid(!!normalizedPhone && /^\+[1-9]\d{1,14}$/.test(normalizedPhone));
    }
  }, [isOpen, initialData]);

  /**
   * isAdminMode   : allowed_emails 페이지 — 이메일 추가 모달
   * isSiteManagerMode : company_page — site Manager 추가 모달
   */
  const isAdminMode = mode === 'admin' || mode === 'adminEdit';
  const isSiteManagerMode = mode === 'siteManager';
  const isAdminEditMode = mode === 'adminEdit';

  const isEmailValid = !!formData.email && validateEmail(formData.email).isValid;

  const isFormValid = (() => {
    if (isAdminMode)
      return !!(
        isEmailValid &&
        formData.company_name &&
        formData.planned_device_limit != null &&
        formData.planned_device_limit >= 1 &&
        formData.planned_duration_months != null &&
        formData.planned_duration_months >= 1
      );
    if (isSiteManagerMode)
      return !!(isEmailValid && formData.user_name && (!formData.phone || phoneValid));
    return false;
  })();

  const modalTitle =
    title || (isAdminMode ? t(i18nKeys.common.menu_allowed_emails) : t(i18nKeys.auth.add));
  const modalDescription =
    description ??
    (isSiteManagerMode
      ? undefined
      : isAdminMode
        ? t(i18nKeys.auth.signup_subscription_invite_add_desc)
        : 'Add a new allowed email address.');

  const handleSubmit = () => {
    if (!isFormValid) return;
    onSubmit(formData);
  };

  return (
    <ModalShell
      isOpen={isOpen}
      title={modalTitle}
      onClose={onClose}
      size="md"
      titleAddon={
        isAdminMode ? (
          <GuideButton guideKey="allowedEmailInvite" iconOnly iconSize={18} />
        ) : undefined
      }
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
            {t(i18nKeys.auth.cancel)}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading || !isFormValid}
          >
            {isAdminEditMode ? t(i18nKeys.auth.save) : t(i18nKeys.auth.add)}
          </Button>
        </>
      }
    >
      {modalDescription && (
        <div data-tour="reg-request-intro">
          <Text
            size="sm"
            color="textSecondary"
            style={{ marginBottom: '1.5rem', textAlign: 'center', lineHeight: 1.6 }}
          >
            {modalDescription.split('\n').map((line, idx) => (
              <React.Fragment key={idx}>
                {line}
                {idx < modalDescription.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </Text>
        </div>
      )}

      <FormStack>
        <FormField data-tour={isAdminMode ? 'reg-request-email' : undefined}>
          <Input
            label={`${t(i18nKeys.auth.e_mail)}${isAdminMode || isSiteManagerMode ? ' *' : ''}`}
            type="email"
            value={formData.email}
            onChange={value => setFormData(prev => ({ ...prev, email: value }))}
            placeholder={t(i18nKeys.auth.e_mail)}
            fullWidth
          />
        </FormField>

        {!isAdminMode && (
          <FormField>
            <Input
              label={`${t(i18nKeys.auth.name)} *`}
              type="text"
              value={formData.user_name || ''}
              onChange={value => setFormData(prev => ({ ...prev, user_name: value }))}
              placeholder={t(i18nKeys.auth.enter_name)}
              fullWidth
            />
          </FormField>
        )}

        {!isSiteManagerMode && (
          <FormField data-tour={isAdminMode ? 'reg-request-company' : undefined}>
            <Input
              label={`${t(i18nKeys.auth.company_name)} *`}
              type="text"
              value={formData.company_name || ''}
              onChange={value => setFormData(prev => ({ ...prev, company_name: value }))}
              placeholder={t(i18nKeys.auth.enter_company_name)}
              fullWidth
            />
          </FormField>
        )}

        {isAdminMode && (
          <>
            <FormField data-tour="reg-request-device-limit">
              <Input
                label={`${t(i18nKeys.auth.allowed_email_planned_device_limit)} *`}
                type="number"
                value={String(formData.planned_device_limit ?? '')}
                onChange={value =>
                  setFormData(prev => ({
                    ...prev,
                    planned_device_limit: value === '' ? undefined : Number(value),
                  }))
                }
                min={1}
                fullWidth
              />
            </FormField>
            <FormField data-tour="reg-request-duration">
              <Input
                label={`${t(i18nKeys.auth.allowed_email_planned_duration_months)} *`}
                type="number"
                value={String(formData.planned_duration_months ?? '')}
                onChange={value =>
                  setFormData(prev => ({
                    ...prev,
                    planned_duration_months: value === '' ? undefined : Number(value),
                  }))
                }
                min={1}
                fullWidth
              />
            </FormField>
          </>
        )}

        {isSiteManagerMode && (
          <FormField>
            <PhoneInput
              label={t(i18nKeys.auth.phone_number)}
              value={formData.phone || ''}
              onChange={(value, isValid) => {
                setFormData(prev => ({ ...prev, phone: value }));
                setPhoneValid(isValid);
              }}
              fullWidth
            />
          </FormField>
        )}

        {(isAdminMode || isSiteManagerMode) && (
          <FormField data-tour={isAdminMode ? 'reg-request-memo' : undefined}>
            <Input
              label={t(i18nKeys.auth.memo)}
              multiline
              rows={3}
              value={formData.notes || ''}
              onChange={value => setFormData(prev => ({ ...prev, notes: value }))}
              fullWidth
            />
          </FormField>
        )}
      </FormStack>
    </ModalShell>
  );
};
